import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';
interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
}
export function SEO({
  title,
  description,
  keywords,
  image = 'https://cdn.magicpatterns.com/uploads/gmziJf2yoMenHZsbfH3Ve2/image.png',
  url,
  type = 'website',
  author = 'NEW ASIA TECH',
  publishedTime,
  modifiedTime
}: SEOProps) {
  const {
    language
  } = useLanguage();
  const siteUrl = 'https://newasiatech.co.id';
  const fullUrl = url ? `${siteUrl}${url}` : siteUrl;
  const defaultTitles = {
    en: 'NEW ASIA TECH - TECHNOLOGY BEYOND CONTROL.',
    id: 'NEW ASIA TECH - TEKNOLOGI MELAMPAUI KENDALI.'
  };
  const defaultDescriptions = {
    en: 'We design future technology systems that are intelligent, autonomous, and ethical by design. Sovereign AI infrastructure, predictive governance systems, and critical asset defense for institutions across Asia.',
    id: 'Kami merancang sistem teknologi masa depan yang cerdas, otonom, dan etis sejak awal. Infrastruktur AI berdaulat, sistem tata kelola prediktif, dan pertahanan aset kritis untuk institusi di seluruh Asia.'
  };
  const defaultKeywords = {
    en: 'sovereign technology, artificial intelligence, AI infrastructure, cyber-physical systems, predictive governance, smart city, defense systems, data sovereignty, neuro-symbolic AI, edge computing, Indonesia technology, Asia tech, NEW ASIA TECH, PT NETKRIDA TUAH CAKRAWALA, technology beyond control',
    id: 'teknologi berdaulat, kecerdasan buatan, infrastruktur AI, sistem cyber-fisik, tata kelola prediktif, kota pintar, sistem pertahanan, kedaulatan data, AI neuro-simbolik, komputasi edge, teknologi Indonesia, teknologi Asia, NEW ASIA TECH, PT NETKRIDA TUAH CAKRAWALA, teknologi melampaui kendali'
  };
  const pageTitle = title || defaultTitles[language];
  const pageDescription = description || defaultDescriptions[language];
  const pageKeywords = keywords || defaultKeywords[language];
  // Ensure image URL is absolute and uses HTTPS
  const absoluteImageUrl = image.startsWith('http') ? image : `${siteUrl}${image}`;
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'NEW ASIA TECH',
    alternateName: 'TECHNOLOGY BEYOND CONTROL',
    legalName: 'PT NETKRIDA TUAH CAKRAWALA',
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    image: absoluteImageUrl,
    foundingDate: '2010',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Savana Cluster, A2, Jl. Karya Bakti, Tengkerang Bar.',
      addressLocality: 'Kec. Marpoyan Damai, Pekanbaru',
      addressRegion: 'Riau',
      postalCode: '28282',
      addressCountry: 'ID'
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
      email: 'contact@newasiatech.co.id',
      availableLanguage: ['English', 'Indonesian']
    },
    sameAs: ['https://netkrida.co.id'],
    description: pageDescription,
    areaServed: {
      '@type': 'Place',
      name: 'Asia'
    },
    knowsAbout: ['Artificial Intelligence', 'Cyber-Physical Systems', 'Sovereign Technology', 'Predictive Governance', 'Smart City Solutions', 'Defense Systems'],
    employee: [{
      '@type': 'Person',
      name: 'Irvannanda',
      jobTitle: 'Commissioner'
    }, {
      '@type': 'Person',
      name: 'Nurfadlan A',
      jobTitle: 'CEO'
    }, {
      '@type': 'Person',
      name: 'N Reza',
      jobTitle: 'COO'
    }]
  };
  const breadcrumbData = url && url !== '/' ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [{
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: siteUrl
    }, {
      '@type': 'ListItem',
      position: 2,
      name: pageTitle.split('|')[0].split('-')[0].trim(),
      item: fullUrl
    }]
  } : null;
  const websiteData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'NEW ASIA TECH',
    alternateName: 'TECHNOLOGY BEYOND CONTROL',
    url: siteUrl,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteUrl}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  };
  return <Helmet data-id="element-119">
      {/* HTML Attributes */}
      <html lang={language} data-id="element-120" />

      {/* Primary Meta Tags */}
      <title data-id="element-121">{pageTitle}</title>
      <meta name="title" content={pageTitle} data-id="element-122" />
      <meta name="description" content={pageDescription} data-id="element-123" />
      <meta name="keywords" content={pageKeywords} data-id="element-124" />
      <meta name="author" content={author} data-id="element-125" />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" data-id="element-126" />
      <meta name="googlebot" content="index, follow" data-id="element-127" />
      <meta name="bingbot" content="index, follow" data-id="element-128" />
      <link rel="canonical" href={fullUrl} data-id="element-129" />

      {/* Alternate Language Links */}
      <link rel="alternate" hrefLang="en" href={`${siteUrl}${url || ''}`} data-id="element-130" />
      <link rel="alternate" hrefLang="id" href={`${siteUrl}${url || ''}`} data-id="element-131" />
      <link rel="alternate" hrefLang="x-default" href={`${siteUrl}${url || ''}`} data-id="element-132" />

      {/* Open Graph / Facebook - CRITICAL FOR WHATSAPP */}
      <meta property="og:type" content={type} data-id="element-133" />
      <meta property="og:url" content={fullUrl} data-id="element-134" />
      <meta property="og:site_name" content="NEW ASIA TECH" data-id="element-135" />
      <meta property="og:title" content={pageTitle} data-id="element-136" />
      <meta property="og:description" content={pageDescription} data-id="element-137" />

      {/* Image Meta Tags - Multiple formats for compatibility */}
      <meta property="og:image" content={absoluteImageUrl} data-id="element-138" />
      <meta property="og:image:secure_url" content={absoluteImageUrl} data-id="element-139" />
      <meta property="og:image:url" content={absoluteImageUrl} data-id="element-140" />
      <meta property="og:image:alt" content={`${pageTitle} - NEW ASIA TECH`} data-id="element-141" />
      <meta property="og:image:type" content="image/png" data-id="element-142" />
      <meta property="og:image:width" content="1200" data-id="element-143" />
      <meta property="og:image:height" content="630" data-id="element-144" />

      {/* Locale */}
      <meta property="og:locale" content={language === 'id' ? 'id_ID' : 'en_US'} data-id="element-145" />
      <meta property="og:locale:alternate" content={language === 'id' ? 'en_US' : 'id_ID'} data-id="element-146" />

      {/* Twitter Card - Uses og:image as fallback */}
      <meta name="twitter:card" content="summary_large_image" data-id="element-147" />
      <meta name="twitter:url" content={fullUrl} data-id="element-148" />
      <meta name="twitter:title" content={pageTitle} data-id="element-149" />
      <meta name="twitter:description" content={pageDescription} data-id="element-150" />
      <meta name="twitter:image" content={absoluteImageUrl} data-id="element-151" />
      <meta name="twitter:image:alt" content={`${pageTitle} - NEW ASIA TECH`} data-id="element-152" />
      <meta name="twitter:site" content="@newasiatech" data-id="element-153" />
      <meta name="twitter:creator" content="@newasiatech" data-id="element-154" />

      {/* WhatsApp specific (uses Open Graph) */}
      <meta property="og:image:width" content="1200" data-id="element-155" />
      <meta property="og:image:height" content="630" data-id="element-156" />

      {/* Article Meta Tags */}
      {type === 'article' && publishedTime && <meta property="article:published_time" content={publishedTime} data-id="element-157" />}
      {type === 'article' && modifiedTime && <meta property="article:modified_time" content={modifiedTime} data-id="element-158" />}
      {type === 'article' && <>
          <meta property="article:author" content={author} data-id="element-159" />
          <meta property="article:section" content="Technology" data-id="element-160" />
          <meta property="article:tag" content="Artificial Intelligence" data-id="element-161" />
          <meta property="article:tag" content="Sovereign Technology" data-id="element-162" />
          <meta property="article:tag" content="Cyber-Physical Systems" data-id="element-163" />
        </>}

      {/* Structured Data */}
      <script type="application/ld+json" data-id="element-164">
        {JSON.stringify(structuredData)}
      </script>

      {breadcrumbData && <script type="application/ld+json" data-id="element-165">
          {JSON.stringify(breadcrumbData)}
        </script>}

      <script type="application/ld+json" data-id="element-166">{JSON.stringify(websiteData)}</script>

      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#0A0E14" data-id="element-167" />
      <meta name="msapplication-TileColor" content="#0A0E14" data-id="element-168" />
      <meta name="msapplication-TileImage" content={absoluteImageUrl} data-id="element-169" />
      <meta name="format-detection" content="telephone=no" data-id="element-170" />
      <meta name="mobile-web-app-capable" content="yes" data-id="element-171" />
      <meta name="apple-mobile-web-app-capable" content="yes" data-id="element-172" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" data-id="element-173" />
      <meta name="apple-mobile-web-app-title" content="NEW ASIA TECH" data-id="element-174" />

      {/* Geo Tags */}
      <meta name="geo.region" content="ID-RI" data-id="element-175" />
      <meta name="geo.placename" content="Pekanbaru" data-id="element-176" />
      <meta name="geo.position" content="0.4798687;101.4313303" data-id="element-177" />
      <meta name="ICBM" content="0.4798687, 101.4313303" data-id="element-178" />

      {/* Social Media Platform IDs */}
      <meta property="fb:app_id" content="YOUR_FACEBOOK_APP_ID" data-id="element-179" />
      <meta name="pinterest-rich-pin" content="true" data-id="element-180" />
    </Helmet>;
}